/**
 * Custom hook for managing entity creation (region, city, vereda).
 * Extracted from FarmDetailsModal.tsx.
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../api/client/axiosClient";
import { ENDPOINTS } from "../../../api/endpoints";
import { locationKeys } from "../../../api/hooks/location.hooks";

interface UseEntityCreationProps {
  selectedRegionId: string;
  selectedCityId: string;
}

interface UseEntityCreationResult {
  createModalOpen: boolean;
  setCreateModalOpen: (value: boolean) => void;
  createModalType: "region" | "city" | "vereda";
  createModalLoading: boolean;
  selectedCountryId: string;
  selectedRegionForCity: string;
  selectedCityForVereda: string;
  selectedCorregimientoForVereda: string;
  setSelectedCountryId: (value: string) => void;
  setSelectedRegionForCity: (value: string) => void;
  setSelectedCityForVereda: (value: string) => void;
  setSelectedCorregimientoForVereda: (value: string) => void;
  handleOpenCreateModal: (type: "region" | "city" | "vereda") => void;
  handleSaveEntity: (data: any) => Promise<void>;
}

export function useEntityCreation({
  selectedRegionId,
  selectedCityId,
}: UseEntityCreationProps): UseEntityCreationResult {
  const queryClient = useQueryClient();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalType, setCreateModalType] = useState<
    "region" | "city" | "vereda"
  >("region");
  const [createModalLoading, setCreateModalLoading] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedRegionForCity, setSelectedRegionForCity] = useState<string>("");
  const [selectedCityForVereda, setSelectedCityForVereda] = useState<string>("");
  const [selectedCorregimientoForVereda, setSelectedCorregimientoForVereda] =
    useState<string>("");

  // Handle opening create modal
  const handleOpenCreateModal = (type: "region" | "city" | "vereda") => {
    setCreateModalType(type);
    setCreateModalOpen(true);
    // Reset form states when opening modal
    setSelectedCountryId("");
    setSelectedRegionForCity("");
    setSelectedCityForVereda("");
    setSelectedCorregimientoForVereda("");
  };

  // Handle saving new entity
  const handleSaveEntity = async (data: any) => {
    setCreateModalLoading(true);
    try {
      switch (createModalType) {
        case "region": {
          // Create JSON string manually to guarantee order
          const regionJson = JSON.stringify({
            name: data.name,
            countryId: data.countryId,
            isActive: data.isActive,
          });

          await apiClient.post(ENDPOINTS.regions.create(), regionJson, {
            headers: {
              "Content-Type": "application/json",
            },
          });
          break;
        }

        case "city": {
          // Create JSON string manually to guarantee order
          const cityJson = JSON.stringify({
            name: data.name,
            regionId: data.regionId,
            isActive: data.isActive,
          });

          await apiClient.post(ENDPOINTS.cities.create(), cityJson, {
            headers: {
              "Content-Type": "application/json",
            },
          });
          break;
        }

        case "vereda": {
          // Create JSON string manually to guarantee order
          const veredaJson = JSON.stringify({
            name: data.name,
            cityId: data.cityId,
            corregimientoId: data.corregimientoId,
            isActive: data.isActive,
          });

          await apiClient.post(ENDPOINTS.veredas.create(), veredaJson, {
            headers: {
              "Content-Type": "application/json",
            },
          });
          break;
        }

        default:
          throw new Error("Tipo de entidad no válido");
      }

      // Invalidate and refetch the appropriate queries
      switch (createModalType) {
        case "region":
          await queryClient.invalidateQueries({
            queryKey: locationKeys.regions(),
          });
          break;
        case "city":
          await queryClient.invalidateQueries({
            queryKey: locationKeys.cities(selectedRegionId),
          });
          break;
        case "vereda":
          await queryClient.invalidateQueries({
            queryKey: locationKeys.veredasByCity(selectedCityId),
          });
          break;
      }

      // Close modal
      setCreateModalOpen(false);

      // Reset form data
      setSelectedCountryId("");
      setSelectedRegionForCity("");
      setSelectedCityForVereda("");
      setSelectedCorregimientoForVereda("");
    } catch (error) {
      console.error("Error saving entity:", error);
    } finally {
      setCreateModalLoading(false);
    }
  };

  return {
    createModalOpen,
    setCreateModalOpen,
    createModalType,
    createModalLoading,
    selectedCountryId,
    selectedRegionForCity,
    selectedCityForVereda,
    selectedCorregimientoForVereda,
    setSelectedCountryId,
    setSelectedRegionForCity,
    setSelectedCityForVereda,
    setSelectedCorregimientoForVereda,
    handleOpenCreateModal,
    handleSaveEntity,
  };
}
